/* After Tempo — site-wide behaviour: the ON → OFF switch and the rolling price-guarantee date. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setOff = (toggle, off) => {
    toggle.classList.toggle('is-off', off);
    if (toggle.getAttribute('role') === 'switch') {
      toggle.setAttribute('aria-checked', String(!off));
    }
  };

  const init = (root = document) => {
    // Decorative switches flip from ON to OFF once, when they come into view.
    const auto = root.querySelectorAll('[data-at-toggle-auto]:not([data-at-ready])');
    if (auto.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const toggle = entry.target;
            observer.unobserve(toggle);
            if (reduced) return setOff(toggle, true);
            window.setTimeout(() => setOff(toggle, true), 900);
          });
        },
        { threshold: 0.6 }
      );
      auto.forEach((toggle) => {
        toggle.dataset.atReady = 'true';
        observer.observe(toggle);
      });
    }

    // Interactive switches: the visitor flips them.
    root.querySelectorAll('button[data-at-toggle]:not([data-at-ready])').forEach((toggle) => {
      toggle.dataset.atReady = 'true';
      toggle.addEventListener('click', () => setOff(toggle, !toggle.classList.contains('is-off')));
    });
  };

  /* ---------- Rolling price-guarantee date ----------
     Today plus N calendar days in Europe/Brussels. This is the only place the date is calculated in the
     browser; every placement is a <time data-at-guarantee-date> from the at-guarantee-date snippet, which
     also sets N (data-days). */
  const GUARANTEE_TZ = 'Europe/Brussels';

  const brusselsNow = (now) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: GUARANTEE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
    const get = (type) => Number(parts.find((part) => part.type === type).value);
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
  };

  // Calendar arithmetic in UTC: month ends, year ends and daylight-saving changes cannot shift the result.
  const guaranteeDate = (days = 2, now = new Date()) => {
    const { year, month, day } = brusselsNow(now);
    return new Date(Date.UTC(year, month - 1, day + days));
  };

  const formatGuaranteeDate = (date, lang = document.documentElement.lang || 'en') =>
    new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : lang, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);

  const renderGuarantee = (root = document, now = new Date()) => {
    root.querySelectorAll('[data-at-guarantee-date]').forEach((el) => {
      const date = guaranteeDate(Number(el.dataset.days) || 2, now);
      const text = formatGuaranteeDate(date);
      const iso = date.toISOString().slice(0, 10);
      if (el.textContent !== text) el.textContent = text;
      if (el.getAttribute('datetime') !== iso) el.setAttribute('datetime', iso);
    });
  };

  // Milliseconds until the next Brussels midnight, by the wall clock.
  const msToBrusselsMidnight = (now = new Date()) => {
    const { hour, minute, second } = brusselsNow(now);
    return Math.max(1000, (86400 - (hour * 3600 + minute * 60 + second)) * 1000 - now.getMilliseconds());
  };

  const watchGuarantee = () => {
    const refresh = () => renderGuarantee();
    const atMidnight = () => {
      refresh();
      window.setTimeout(atMidnight, msToBrusselsMidnight() + 250);
    };
    refresh();
    window.setTimeout(atMidnight, msToBrusselsMidnight() + 250);
    // Safety net for throttled background tabs, sleeping laptops and the two daylight-saving days.
    window.setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refresh();
    });
    window.addEventListener('pageshow', refresh);
    window.addEventListener('focus', refresh);
  };

  window.AtGuarantee = { date: guaranteeDate, format: formatGuaranteeDate, render: renderGuarantee, msToMidnight: msToBrusselsMidnight };

  document.addEventListener('DOMContentLoaded', () => {
    init();
    watchGuarantee();
  });
  // Re-initialise when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    renderGuarantee(event.target);
  });
})();
